import React, {useEffect, useState} from 'react';

import {
    LinearProgress,
    styled,
    Card,
    CardHeader,
    CardContent,
    Avatar,
    Tooltip,
    Typography,
} from "@mui/material";
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ReportIcon from '@mui/icons-material/Report';
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

import useDataSource from "../../../../hooks/useDataSource";
import {compareValues} from "../../../../utilities/DataUtilities";
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import SettingsForm from "../settings/SettingsForm";
import {appSetModalContent} from "../../../../redux/app/appActions";
import {findComponent} from "../../../../redux/page/pageActions";


const PREFIX = 'TrendsInsights';

const classes = {
    root: `${PREFIX}-root`,
    linearProgress: `${PREFIX}-linearProgress`,
    card: `${PREFIX}-card`,
    cardHeader: `${PREFIX}-cardHeader`,
    trendGroup: `${PREFIX}-trendGroup`,
    trendInsight: `${PREFIX}-trendInsight`,
    insight: `${PREFIX}-insight`,
    trendRed: `${PREFIX}-trendRed`,
    trendYellow: `${PREFIX}-trendYellow`,
    trendGreen: `${PREFIX}-trendGreen`,
    icon: `${PREFIX}-icon`,
    thumbIcon: `${PREFIX}-thumbIcon`,
    bulletIcon: `${PREFIX}-bulletIcon`
};

const Root = styled('div')((
    {
        theme
    }
) => ({
    [`&.${classes.root}`]: {
        height: 'calc(100% - 1px)'
    },

    [`& .${classes.linearProgress}`]: {
        height: '1px'
    },

    [`& .${classes.card}`]: {
        height: '100%',
        overflow: 'auto',
        boxShadow: 'none'
    },

    [`& .${classes.cardHeader}`]: {
        padding: '8px 8px 0px 8px',
        alignItems: 'start',
        textAlign: 'left',
        textTransform: 'none',
    },

    [`& .${classes.trendGroup}`]: {
        marginBottom: '20px'
    },

    [`&.${classes.trendInsight}`]: {
        margin: '5px'
    },

    [`& .${classes.insight}`]: {
        // marginLeft: '10px'
    },

    [`& .${classes.trendRed}`]: {
        float: 'left',
        marginTop: '2px',
        marginRight: '11px',
        width: '16px',
        height: '16px',
        color: '#fff',
        backgroundColor: 'red'
    },

    [`& .${classes.trendYellow}`]: {
        float: 'left',
        marginTop: '2px',
        marginRight: '11px',
        width: '16px',
        height: '16px',
        color: '#fff',
        backgroundColor: '#E1AD01'
    },

    [`& .${classes.trendGreen}`]: {
        float: 'left',
        marginTop: '2px',
        marginRight: '11px',
        width: '16px',
        height: '16px',
        color: '#fff',
        backgroundColor: 'green'
    },

    [`& .${classes.icon}`]: {
        width: '16px',
        height: '16px',
    },

    [`& .${classes.thumbIcon}`]: {
        width: '14px',
        height: '14px',
    },

    [`& .${classes.bulletIcon}`]: {
        width: '8px',
        height: '8px',
        marginTop: '4px',
        marginLeft: '4px',
        marginRight: '14px'
    }
}));

export default function TrendsInsights({configuration, xId, onLoading, onLoaded}) {

	const dispatch = useDispatch();
	const { t } = useTranslation();

    // App State
	const pageID = useSelector(state => state.app.pageID);
	const editXId = useSelector(state => state.page.componentEdit.xId);
	const componentUpdated = useSelector(state => state.page.componentUpdated);
	const componentWrapperEdit = useSelector(state => state.page.componentWrapperEdit);
	const pages = useSelector(state => state.page.pages);
	const pageConfigs = useSelector(state => state.config.data.pageConfigs);
	const activeRole = useSelector(state => state.persist.activeRoleName);

    // Component State
	const [compConfig, setCompConfig] = useState(configuration);
	const [title, setTitle] = useState(compConfig.name?.title ? compConfig.name.title : '');
	const [subtitle, setSubTitle] = useState(compConfig.name?.subtitle ? compConfig.name.subtitle : '');
    const [trendsInsights, setTrendsInsights] = useState([]);
    const [showProgress, setShowProgress] = useState(isFunction(onLoading) ? false : true);
	const [cardHeaderStyle, setCardHeaderStyle] = useState({});
	const [cardContentStyle, setCardContentStyle] = useState({});

	const dataSourceName = compConfig.dataSource?.name ? compConfig.dataSource?.name : '';
	const styles = compConfig?.element?.styles;

	const dataSource = useDataSource(dataSourceName);

    // Loading
    useEffect(() => {
        if (dataSource.fetching === true) {
            if (isFunction(onLoading)) {
                onLoading();
            }
        }
    }, [dataSource.fetching]);

    // Loaded
    useEffect(() => {
        if (dataSource.fetching === false && dataSource.fetched === true) {
            if (isFunction(onLoaded)) {
                onLoaded();
            }
        }
    }, [dataSource.fetched]);

	// Did Component Wrapper Trigger Redux State Change?
	useEffect(() => {
		if (componentWrapperEdit.parentId && componentWrapperEdit.childId && xId) {
			if ((componentWrapperEdit.parentId === xId) || (componentWrapperEdit.childId === xId)) {
				const page = pages.find(page => page.name === pageID);
				for (let i = 0; i < pageConfigs.length; i++ ) {
					const roleConfig = page.roles.find(r => r.name === activeRole)[pageConfigs[i]];
					if (roleConfig) {
						const findResult = findComponent(xId, componentWrapperEdit.childId === xId ? componentWrapperEdit.parentId : null, roleConfig.configuration.components);
						if (findResult) {
							setCompConfig(findResult.configuration);
							break;
						}
					}
				}
			}
		}
	},[componentWrapperEdit]);

	// Component Configuration (compConfig) changed, reset state
	useEffect(() => {
		setTitle(compConfig.name?.title ? compConfig.name.title : '');
		setSubTitle(compConfig.name?.subtitle ? compConfig.name.subtitle : '');
	},[compConfig]);

	useEffect(() => {
		resizeCard();
	},[title, subtitle]);

	// componentUpdated & Resized
	useEffect(() => {
		if (xId) {
			if (componentUpdated.id === xId) {
				setCompConfig(componentUpdated.config);
			}
		}
	}, [componentUpdated]);

	// xEdit
	useEffect(() => {
		if (editXId) {
			if ((editXId === xId) && (pageID)) {

				const placeholders = {
					title: title,
					subtitle: subtitle
				}

				const settingsConfig = {
					title: 'Trends & Insights Configuration',
					parent: {
						minWidth: '400px',
						maxWidth: '400px',
						minHeight: '520px',
						maxHeight: '520px'
					},
					paper: null,
					button: null,
					content: <SettingsForm pageID={pageID} xId={xId} configuration={compConfig} schemaType={'TrendsInsights'} placeholders={placeholders}/>,
					tooltipContent: null
				}
				dispatch(appSetModalContent(settingsConfig));
			}
		}
	},[editXId, pageID]);

    // Data
    useEffect(() => {
        if (dataSource.data && dataSource.fetched) {
            // Get Groups
            const uniqueGroups = [...new Set(dataSource.data.map(item => item.grouping))];
            const sortedTrends = dataSource.data.sort(compareValues('orderby'));
            const groupedTrends = uniqueGroups.map(g => ({grouping: g, trends: (sortedTrends.filter(p => (g === p.grouping)))}));
            const keyTrends = groupedTrends.map(n => ({grouping: setGroupHeadingLayout(n.grouping), trends: n.trends.map((x, i) => (setTrendLayout({ key: i, trend: x.trend, insight: x.insight })))}));
            setTrendsInsights(keyTrends);
        } else {
            setTrendsInsights([]);
        }
    }, [dataSource.data, classes]);

    const getTrendIcon = (trend) => {
        let icon;

        switch (trend) {
            case "Good":
                icon = <Tooltip title="Good" placement="top-start" arrow><Avatar className={classes.trendGreen}><ThumbUpIcon className={classes.thumbIcon}/></Avatar></Tooltip>;
                break;
            case "Good - Up":
                icon = <Tooltip title="Good - Up" placement="top-start" arrow><Avatar className={classes.trendGreen}><TrendingUpIcon className={classes.icon}/></Avatar></Tooltip>;
                break;
            case "Good - NC":
                icon = <Tooltip title="Good - NC" placement="top-start" arrow><Avatar className={classes.trendGreen}><TrendingFlatIcon className={classes.icon}/></Avatar></Tooltip>;
                break;
            case "Good - Down":
                icon = <Tooltip title="Good - Down" placement="top-start" arrow><Avatar className={classes.trendGreen}><TrendingDownIcon className={classes.icon}/></Avatar></Tooltip>;
                break;
            case "Bad":
                icon = <Tooltip title="Bad" placement="top-start" arrow><Avatar className={classes.trendRed}><ThumbDownIcon className={classes.thumbIcon}/></Avatar></Tooltip>;;
                break;
            case "Bad - Up":
                icon = <Tooltip title="Bad - Up" placement="top-start" arrow><Avatar className={classes.trendRed}><TrendingUpIcon className={classes.icon}/></Avatar></Tooltip>;
                break;
            case "Bad - NC":
                icon = <Tooltip title="Bad - NC" placement="top-start" arrow><Avatar className={classes.trendRed}><TrendingFlatIcon className={classes.icon}/></Avatar></Tooltip>;
                break;
            case "Bad - Down":
                icon = <Tooltip title="Bad - Down" placement="top-start" arrow><Avatar className={classes.trendRed}><TrendingDownIcon className={classes.icon}/></Avatar></Tooltip>;
                break;
            case "Warning":
                icon = <Tooltip title="Warning" placement="top-start" arrow><Avatar className={classes.trendYellow}><ReportIcon className={classes.icon}/></Avatar></Tooltip>;
                break;
            case "Warning - Up":
                icon = <Tooltip title="Warning - Up" placement="top-start" arrow><Avatar className={classes.trendYellow}><TrendingUpIcon className={classes.icon}/></Avatar></Tooltip>;
                break;
            case "Warning - NC":
                icon = <Tooltip title="Warning - NC" placement="top-start" arrow><Avatar className={classes.trendYellow}><TrendingFlatIcon className={classes.icon}/></Avatar></Tooltip>;
                break;
            case "Warning - Down":
                    icon = <Tooltip title="Warning - Down" placement="top-start" arrow><Avatar className={classes.trendYellow}><TrendingDownIcon className={classes.icon}/></Avatar></Tooltip>;
                break;
            case "Bullet":
                icon = <FiberManualRecordIcon className={classes.bulletIcon}/>;
                break;
            case "No Trend":
                icon = "";
                break;
            default:
                icon = "";
        }

        return icon;
    };

    const setGroupHeadingLayout = (grouping) => {
        // console.log(grouping);
        return (
            <Typography variant="body2" color="textSecondary">{grouping}</Typography>
        );
    }

    const setTrendLayout = (trendAndInsight) => {
        return (
            <div key={trendAndInsight.key} className={classes.trendInsight}>
                {getTrendIcon(trendAndInsight.trend)}
                <Typography variant="caption" className={classes.insight} dangerouslySetInnerHTML={{ __html: trendAndInsight.insight }}/>
            </div>
        );
    }

    function isFunction(functionToCheck) {
        return functionToCheck && {}.toString.call(functionToCheck) === '[object Function]';
    }

	const resizeCard = () => {

		const newCardHeaderStyle = Object.assign({}, {...styles?.cardHeader});
		const newCardContentStyle = Object.assign({}, {...styles?.cardContent
			,height: 'calc(100% - 49px)'
			,overflow: 'auto'
			,padding: '8px'
		});

		if (title && subtitle) {
			// Default Style
		} else if (title) {
			// Title Only
			newCardHeaderStyle.height = '29px';
			newCardContentStyle.height = 'calc(100% - 29px)';
		} else if (subtitle) {
			// Subtitle Only
			newCardHeaderStyle.height = '29px';
			newCardContentStyle.height = 'calc(100% - 29px)';
		} else {
			// No title
			newCardHeaderStyle.height = '0px';
			newCardHeaderStyle.padding = '0px';
			newCardContentStyle.height = '100%';
		}
		setCardHeaderStyle(newCardHeaderStyle);
		setCardContentStyle(newCardContentStyle);
	}

    return (
        <Root className={classes.root}>
            {showProgress && dataSource.fetching &&
            <LinearProgress className={classes.linearProgress}/>
            }
            {showProgress && !dataSource.fetching &&
            <div className={classes.linearProgress}></div>
            }
            <Card className={classes.card} style={{...styles?.card}}>
                <CardHeader className={classes.cardHeader} style={cardHeaderStyle}
                            titleTypographyProps={{color: 'textPrimary', variant: 'subtitle2'}}
                            title={t(title)}
                            subheaderTypographyProps={{color: 'textSecondary', variant: 'body2'}}
                            subheader={t(subtitle)}>
				</CardHeader>
                <CardContent className={classes.cardContent} style={cardContentStyle}>
                    {trendsInsights &&
                        trendsInsights.map((p, i) => {
                            return (
                                <div key={i} className={classes.trendGroup}>
                                    {p.grouping}
                                    {p.trends}
                                </div>
                            );
                        })
                    }
                </CardContent>
            </Card>

        </Root>
    );
}
